import { useDialog } from '@wui/common-library'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, type Ref } from 'vue'

import { getFlatCaretOffset } from '@/shared/utils/contenteditable-selection'

import ChatTextFormatLinkDialog from '../ui/chat-text-format-link-dialog.vue'

import { applyTextEmoticons } from './apply-text-emoticons'
import {
  applyInlineFormatCommand,
  cleanEditorFormatPlaceholders,
  createRangeFromEditorSelectionSnapshot,
  ensureInactiveFormatCaretOutside,
  ensureTypingFormatCaret,
  handleInactiveFormatBeforeInput,
  getEditorSelectedText,
  getSelectedLinkElement,
  insertLinkAtSelection,
  queryActiveTextFormats,
  removeLinkElement,
  restoreEditorSelection,
  reconcileTypingFormatDom,
  restoreEditorSelectionSnapshot,
  saveEditorSelection,
  snapshotEditorSelection,
  preparePlainTextInsertAtCaret,
  updateLinkElement,
  type EditorSelectionSnapshot,
} from './text-format-commands'
import { resolveTextFormatCommandFromKeyboardEvent } from './text-format-hotkeys'
import { ActiveTextFormats, InlineFormatCommand, TextFormatLinkDialogResult, TypingFormatCommand } from './types'

const createInactiveTypingFormats = (): Record<TypingFormatCommand, boolean> => ({
  bold: false,
  italic: false,
  underline: false,
  strikethrough: false,
  code: false,
})

const isTypingFormatCommand = (command: InlineFormatCommand): command is TypingFormatCommand => {
  return command !== 'removeFormat'
}

export const useChatTextFormat = (
  editorRef: Ref<HTMLElement | undefined>,
  syncEditorContent?: () => void,
) => {
  const isToolbarVisible = ref(false)
  const typingFormats = ref(createInactiveTypingFormats())
  const typingFormatAnchorFlatOffset = ref<number | null>(null)
  const activeFormats = ref<ActiveTextFormats>({
    ...createInactiveTypingFormats(),
    link: false,
  })
  const { showDialog } = useDialog<TextFormatLinkDialogResult | false>()

  let savedRange: Range | null = null
  let savedLinkElement: HTMLAnchorElement | null = null
  let pendingToolbarSelection: Range | null = null
  let lastEditorSelection: EditorSelectionSnapshot | null = null

  const rememberEditorSelection = () => {
    const snapshot = snapshotEditorSelection(editorRef.value)
    if (snapshot) {
      lastEditorSelection = snapshot
    }
  }

  const mergeActiveFormats = () => {
    const domFormats = queryActiveTextFormats(editorRef.value)

    activeFormats.value = {
      bold: typingFormats.value.bold || domFormats.bold,
      italic: typingFormats.value.italic || domFormats.italic,
      underline: typingFormats.value.underline || domFormats.underline,
      strikethrough: typingFormats.value.strikethrough || domFormats.strikethrough,
      code: typingFormats.value.code || domFormats.code,
      link: domFormats.link,
    }
  }

  const refreshActiveFormats = () => {
    mergeActiveFormats()
  }

  const onSelectionChange = () => {
    rememberEditorSelection()
    if (!isToolbarVisible.value) return
    refreshActiveFormats()
  }

  onMounted(() => {
    document.addEventListener('selectionchange', onSelectionChange)
  })

  onBeforeUnmount(() => {
    document.removeEventListener('selectionchange', onSelectionChange)
  })

  const clearTypingFormats = () => {
    typingFormats.value = createInactiveTypingFormats()
    typingFormatAnchorFlatOffset.value = null

    const editor = editorRef.value
    if (!editor) return

    preparePlainTextInsertAtCaret(editor, typingFormats.value)
    rememberEditorSelection()
    syncEditorContent?.()
  }

  const toggleToolbar = () => {
    isToolbarVisible.value = !isToolbarVisible.value
    if (isToolbarVisible.value) {
      refreshActiveFormats()
      return
    }

    clearTypingFormats()
    refreshActiveFormats()
  }

  const prepareFormat = () => {
    const editor = editorRef.value
    if (!editor) return

    reconcileTypingFormatDom(editor, typingFormats.value, typingFormatAnchorFlatOffset.value)

    const liveSelection = saveEditorSelection(editor)
    const snapshotSelection = createRangeFromEditorSelectionSnapshot(editor, lastEditorSelection)

    pendingToolbarSelection = liveSelection ?? snapshotSelection

    if (!pendingToolbarSelection) {
      const endRange = document.createRange()
      endRange.selectNodeContents(editor)
      endRange.collapse(false)
      pendingToolbarSelection = endRange
    }
  }

  const applyFormat = (command: InlineFormatCommand) => {
    const editor = editorRef.value
    if (!editor) return

    reconcileTypingFormatDom(editor, typingFormats.value, typingFormatAnchorFlatOffset.value)

    const selection = pendingToolbarSelection
      ?? saveEditorSelection(editor)
      ?? createRangeFromEditorSelectionSnapshot(editor, lastEditorSelection)
    const fallbackRange = createRangeFromEditorSelectionSnapshot(editor, lastEditorSelection)
    const domFormats = queryActiveTextFormats(editor)
    const turnOff = isTypingFormatCommand(command) && (
      typingFormats.value[command]
      || domFormats[command]
    )

    const flatOffsetBeforeApply = getFlatCaretOffset(editor)

    const result = applyInlineFormatCommand(
      editor,
      command,
      selection,
      {
        turnOff,
        fallbackRange,
      },
    )

    pendingToolbarSelection = null

    if (result?.format === 'all') {
      typingFormats.value = createInactiveTypingFormats()
      typingFormatAnchorFlatOffset.value = null
    } else if (result) {
      typingFormats.value[result.format] = result.enabled
      if (result.enabled && isTypingFormatCommand(result.format)) {
        typingFormatAnchorFlatOffset.value = flatOffsetBeforeApply ?? 0
      } else if (!result.enabled && isTypingFormatCommand(result.format)) {
        typingFormatAnchorFlatOffset.value = null
      }
    } else if (turnOff && isTypingFormatCommand(command)) {
      typingFormats.value[command] = false
      typingFormatAnchorFlatOffset.value = null
    }

    const postApplySnapshot = snapshotEditorSelection(editor)

    nextTick(() => {
      restoreEditorSelectionSnapshot(editor, postApplySnapshot)

      if (!turnOff && result?.enabled && isTypingFormatCommand(command)) {
        ensureTypingFormatCaret(editor, command)
        syncEditorContent?.()
      } else {
        syncEditorContent?.()
        preparePlainTextInsertAtCaret(editor, typingFormats.value)
      }

      rememberEditorSelection()
      refreshActiveFormats()
    })
  }

  const openLinkDialog = async () => {
    const editor = editorRef.value
    if (!editor) return

    savedRange = saveEditorSelection(editor)
    savedLinkElement = getSelectedLinkElement(editor)

    const selectedText = savedLinkElement?.textContent?.trim()
      ?? getEditorSelectedText(editor)
    const initialUrl = savedLinkElement?.getAttribute('href') ?? ''

    const result = await showDialog(ChatTextFormatLinkDialog, {
      initialText: selectedText,
      initialUrl,
      isEditing: Boolean(savedLinkElement),
      hasOverlay: true,
    })

    if (!result || typeof result !== 'object') {
      savedRange = null
      savedLinkElement = null
      return
    }

    let updated = false

    if ('remove' in result) {
      if (savedLinkElement && editor.contains(savedLinkElement)) {
        updated = removeLinkElement(savedLinkElement)
      }
    } else if (savedLinkElement && editor.contains(savedLinkElement)) {
      updated = updateLinkElement(savedLinkElement, result.url, result.text)
    } else {
      restoreEditorSelection(editor, savedRange)
      updated = insertLinkAtSelection(editor, result.url, result.text)
    }

    savedRange = null
    savedLinkElement = null

    if (updated) {
      syncEditorContent?.()
    }

    refreshActiveFormats()
  }

  const handleEditorBeforeInput = (event: Event) => {
    const editor = editorRef.value
    if (!editor) return

    if (event instanceof InputEvent && handleInactiveFormatBeforeInput(editor, typingFormats.value, event)) {
      rememberEditorSelection()
      syncEditorContent?.()
      refreshActiveFormats()
      return
    }

    ensureInactiveFormatCaretOutside(editor, typingFormats.value)
    reconcileTypingFormatDom(editor, typingFormats.value, typingFormatAnchorFlatOffset.value)
  }

  const handleEditorInput = () => {
    rememberEditorSelection()

    const editor = editorRef.value
    const reconciled = reconcileTypingFormatDom(
      editor,
      typingFormats.value,
      typingFormatAnchorFlatOffset.value,
    )

    nextTick(() => {
      const placeholdersChanged = cleanEditorFormatPlaceholders(editor)
      const emoticonsChanged = applyTextEmoticons(editor)

      if (reconciled || placeholdersChanged || emoticonsChanged) {
        syncEditorContent?.()
      }

      refreshActiveFormats()
    })
  }

  const handleEditorKeyDown = (event: KeyboardEvent): boolean => {
    const command = resolveTextFormatCommandFromKeyboardEvent(event)
    if (!command) return false

    event.preventDefault()

    if (command === 'link') {
      prepareFormat()
      void openLinkDialog()
      return true
    }

    prepareFormat()
    applyFormat(command)
    return true
  }

  const isTogglePressed = computed(() => isToolbarVisible.value)

  return {
    isToolbarVisible,
    isTogglePressed,
    activeFormats,
    toggleToolbar,
    prepareFormat,
    applyFormat,
    openLinkDialog,
    handleEditorBeforeInput,
    handleEditorInput,
    handleEditorKeyDown,
  }
}
